import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletIdState] = useState(localStorage.getItem("mbp_outlet"));

  const loadOutlets = useCallback(async () => {
    const { data } = await api.get("/staff/outlets");
    setOutlets(data);
    setOutletIdState((cur) => (data.find((o) => o.id === cur) ? cur : data[0]?.id || null));
    return data;
  }, []);

  useEffect(() => {
    api.get("/auth/me").then(async (r) => { await loadOutlets(); setUser(r.data); }).catch(() => setUser(false));
    const out = () => setUser(false);
    window.addEventListener("mbp-logout", out);
    return () => window.removeEventListener("mbp-logout", out);
  }, [loadOutlets]);

  const setOutletId = (id) => {
    localStorage.setItem("mbp_outlet", id);
    setOutletIdState(id);
  };
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    await loadOutlets();
    setUser(data);
    return data;
  };
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };
  const updateProfile = async (profile) => {
    const { data } = await api.patch('/auth/profile', profile);
    setUser(data);
    return data;
  };
  const outlet = outlets.find((o) => o.id === outletId) || null;
  useEffect(() => {
    if (!user) return;
    const refresh = () => loadOutlets().catch(e => { if (e.response?.status === 403) setUser(false); });
    const timer = setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    return () => { clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [user, loadOutlets]);
  const isOwner = !!user && user.roles?.some((r) => r.role === "owner" && !r.outlet_id);
  return (
    <AuthCtx.Provider value={{ user, outlets, outlet, outletId, setOutletId, login, logout, loadOutlets, isOwner, updateProfile }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function RequireAuth({ children, minRole, excludeRoles = [] }) {
  const { user, outlet } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="grid min-h-screen place-items-center bg-slate-950 text-slate-300" data-testid="auth-loading">Loading…</div>;
  if (!user) return <Navigate to={`/staff/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (outlet && excludeRoles.includes(outlet.my_role)) return <Navigate to="/staff/bills" replace />;
  if (minRole && outlet && rank(outlet.my_role) < rank(minRole)) return <Navigate to="/staff" replace />;
  return children;
}

export const rank = (r) => ({ kitchen: 1, cashier: 2, manager: 3, owner: 4 })[r] || 0;
